import { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { doc, setDoc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import UnitCard            from "../components/UnitCard";
import { gradeUnits }      from "../data/gradeUnits";
import GamePage            from "./GamePage";      // mini-game component

export default function DashboardPage() {
  /* ---------------- state ---------------- */
  const [grade, setGrade] = useState("1");
  const [current, setCurrent] = useState("");    // unitId currently playing
  const [progress, setProgress] = useState({});  // User progress for each unit
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  /* ---------------- firestore: load user data on mount ---------------- */
  useEffect(() => {
    async function loadUserData() {
      if (!auth.currentUser) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        
        // Load user's grade
        const userRef = doc(db, "users", auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setGrade(userData.grade || "1");
          
          // Load progress data
          const progressRef = collection(db, "progress");
          const progressQuery = query(
            progressRef, 
            where("userId", "==", auth.currentUser.uid)
          );
          
          const progressSnap = await getDocs(progressQuery);
          const userProgress = {};
          
          progressSnap.forEach(doc => {
            const data = doc.data();
            // Set status based on completion
            if (data.completedAt) {
              userProgress[data.unitId] = "completed";
            } else if (data.startedAt) {
              userProgress[data.unitId] = "inProgress";
            }
          });
          
          setProgress(userProgress);
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      } finally {
        setLoading(false);
      }
    }
    
    loadUserData();
  }, [navigate]);

  /* ---------------- firestore: save grade when it changes ---------- */
  useEffect(() => {
    async function updateGrade() {
      if (!auth.currentUser) return;
      
      const ref = doc(db, "users", auth.currentUser.uid);
      await setDoc(ref, { grade }, { merge: true });
    }
    
    updateGrade();
  }, [grade]);

  /* ---------------- handle unit selection ---------------- */
  async function handleUnitClick(unitId) {
    try {
      // Record that the user started this unit if not already in progress
      if (!progress[unitId]) {
        const progressRef = doc(db, "progress", `${auth.currentUser.uid}_${unitId}`);
        await setDoc(progressRef, {
          userId: auth.currentUser.uid,
          unitId: unitId,
          startedAt: new Date(),
          status: "inProgress"
        }, { merge: true });
        
        // Update local state
        setProgress(prev => ({
          ...prev,
          [unitId]: "inProgress"
        }));
      }
      
      // Set current unit to show game page
      setCurrent(unitId);
    } catch (error) {
      console.error("Error starting unit:", error);
    }
  }

  /* ---------------- handle game completion ---------------- */
  async function handleGameComplete(unitId, score) {
    try {
      // Update progress to completed
      const progressRef = doc(db, "progress", `${auth.currentUser.uid}_${unitId}`);
      await setDoc(progressRef, {
        completedAt: new Date(),
        score: score,
        status: "completed"
      }, { merge: true });
      
      // Update local state
      setProgress(prev => ({
        ...prev,
        [unitId]: "completed"
      }));
      
      // Clear current to return to dashboard
      setCurrent("");
    } catch (error) {
      console.error("Error completing unit:", error);
    }
  }

  /* ---------------- play view ---------------- */
  if (current) {
    return (
      <GamePage 
        unitId={current} 
        onDone={(score) => handleGameComplete(current, score)} 
      />
    );
  }

  /* ---------------- dashboard view ---------------- */
  const units = gradeUnits[grade];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-6">
      <h1 className="text-3xl font-bold mb-4">MathBoss – Grade {grade}</h1>

      <div className="mb-4">
        <label className="mr-2 font-medium">Select Grade:</label>
        <select
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          className="border px-2 py-1 rounded"
        >
          {[1, 2, 3, 4, 5, 6].map((g) => (
            <option key={g}>{g}</option>
          ))}
        </select>
      </div>

      <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(120px,1fr))] max-w-3xl w-full">
        {units.map((u) => {
          // Default to unlocked for first unit, otherwise check previous unit
          let status = progress[u.id] || 
                      (u.order === 1 ? "unlocked" : 
                       (progress[units.find(unit => unit.order === u.order - 1)?.id] === "completed" ? 
                        "unlocked" : "locked"));
          
          return (
            <UnitCard
              key={u.id}
              unit={u}
              status={status}
              onPlay={() => handleUnitClick(u.id)}
            />
          );
        })}
      </div>
    </div>
  );
}
